import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { ChevronDown, ChevronRight, Loader2, Pencil, Sparkles } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ── Philosophy catalogue ─────────────────────────────────────
// Core questions everyone answers + an advanced catalogue of optional
// rules. Every selection composes one policy sentence; the AI drafter
// expands the composed brief into a full house-view document.

export const PHILOSOPHY_OPTIONS = {
  style: [
    { v: 'deep_value', label: 'Deep value', brief: 'We are a deep-value desk: we buy quality businesses only when they trade well below intrinsic worth, and would rather miss a rally than overpay.' },
    { v: 'quality_growth', label: 'Quality growth (GARP)', brief: 'We buy durable compounders with high returns on capital and visible growth, at reasonable (not cheap) prices.' },
    { v: 'momentum', label: 'Momentum / trend', brief: 'We buy strength and sell weakness: earnings revisions and price momentum persist longer than the market prices in.' },
    { v: 'dividend', label: 'Dividend income', brief: 'We own cash-generative businesses that return capital through growing, uninterrupted dividends, held indefinitely.' },
    { v: 'contrarian', label: 'Contrarian / special situations', brief: 'We hunt where consensus is most wrong: hated sectors, forced sellers, post-crisis recoveries with intact balance sheets.' },
    { v: 'conservative', label: 'Capital preservation first', brief: 'Capital preservation comes before returns: we accept lower upside for materially lower drawdown risk.' },
  ],
  valuation: [
    { v: 'strict', label: 'Strict — never above 25x P/E', brief: 'Hard valuation ceiling: no entries above 25x trailing earnings, regardless of the growth story.' },
    { v: 'moderate', label: 'Moderate — up to 40x for quality', brief: 'Valuation-aware: up to 40x trailing earnings acceptable for exceptional franchises, cheaper preferred.' },
    { v: 'flexible', label: 'Flexible — quality over multiples', brief: 'Valuation is a constraint, not a signal: we pay up for quality and momentum when the business case is strong.' },
  ],
  horizon: [
    { v: 'short', label: '3–9 months', brief: 'Horizon 3-9 months; positions must have a near-term catalyst.' },
    { v: 'medium', label: '1–2 years', brief: 'Horizon 1-2 years; we underwrite the next earnings cycle, not the next decade.' },
    { v: 'long', label: '3–5 years', brief: 'Horizon 3-5 years; we underwrite businesses, not quarters, and ignore momentum noise.' },
    { v: 'forever', label: 'Indefinite (buy & hold)', brief: 'Indefinite holding period; we exit only on structural impairment of the thesis.' },
  ],
  risk: [
    { v: 'conservative', label: 'Conservative', brief: 'Risk posture conservative: prefer net-cash or lightly levered balance sheets; tight stops; cash is a position.' },
    { v: 'balanced', label: 'Balanced', brief: 'Risk posture balanced: normal market risk acceptable, leverage within sector norms.' },
    { v: 'aggressive', label: 'Aggressive', brief: 'Risk posture aggressive: concentrated bets and volatility tolerated when conviction is high.' },
  ],
  maxPos: [
    { v: '3', label: 'Max 3% NAV / position' },
    { v: '5', label: 'Max 5% NAV / position' },
    { v: '8', label: 'Max 8% NAV / position' },
  ],
};

export const EXCLUSION_OPTIONS = [
  { v: 'loss_making', label: 'Loss-making companies' },
  { v: 'high_leverage', label: 'Highly levered names' },
  { v: 'pledging', label: 'Promoter pledging' },
  { v: 'psu', label: 'PSU names' },
  { v: 'fno', label: 'F&O-driven names' },
  { v: 'smallcap', label: 'Small caps (<₹10k cr)' },
];

// Advanced rules — every field defaults to "No preference" and emits no
// policy text unless the user picks something. These are the levers an
// institution would argue about in an IC meeting.
const NP = { v: 'none', label: 'No preference', brief: null };

export const ADVANCED_GROUPS = [
  {
    title: 'Valuation & entry price',
    fields: [
      { key: 'peCeiling', label: 'P/E ceiling', options: [NP,
        { v: 'pe15', label: 'Never above 15x', brief: 'Hard ceiling of 15x trailing P/E at entry — no exceptions for growth stories.' },
        { v: 'pe25', label: 'Never above 25x', brief: 'Hard ceiling of 25x trailing P/E at entry.' },
        { v: 'pe40', label: 'Up to 40x for quality', brief: 'Up to 40x trailing P/E acceptable for exceptional franchises only.' },
        { v: 'no_ceiling', label: 'No ceiling', brief: 'No P/E ceiling — valuation is weighed, never a hard veto.' },
      ] },
      { key: 'pbCeiling', label: 'P/B for financials', options: [NP,
        { v: 'pb15', label: 'Max 1.5x book', brief: 'Financials must trade below 1.5x book value at entry.' },
        { v: 'pb3', label: 'Max 3x book', brief: 'Financials must trade below 3x book value at entry.' },
        { v: 'ignore', label: 'Judge on ROE instead', brief: 'For financials we judge on sustainable ROE and asset quality, not the book multiple.' },
      ] },
      { key: 'fcfYield', label: 'Free cash flow', options: [NP,
        { v: 'positive', label: 'Must be FCF positive', brief: 'Companies must generate positive free cash flow on a trailing basis — no cash burners.' },
        { v: 'yield4', label: 'FCF yield > 4%', brief: 'Entry requires a trailing free-cash-flow yield above 4%.' },
        { v: 'tolerant', label: 'Burn OK if funded', brief: 'Cash burn is acceptable when fully funded and on a credible path to profitability.' },
      ] },
      { key: 'marginOfSafety', label: 'Margin of safety', options: [NP,
        { v: 'mos20', label: '≥20% below fair value', brief: 'Entries require at least a 20% discount to our estimate of fair value.' },
        { v: 'mos35', label: '≥35% below fair value', brief: 'Entries require at least a 35% discount to our estimate of fair value.' },
      ] },
    ],
  },
  {
    title: 'Quality & profitability',
    fields: [
      { key: 'roceFloor', label: 'ROCE floor', options: [NP,
        { v: 'roce12', label: 'ROCE > 12%', brief: 'Minimum 12% return on capital employed, sustained across the cycle.' },
        { v: 'roce18', label: 'ROCE > 18%', brief: 'Minimum 18% return on capital employed, sustained across the cycle.' },
        { v: 'roce25', label: 'ROCE > 25%', brief: 'Only elite capital allocators: minimum 25% return on capital employed.' },
      ] },
      { key: 'profitHistory', label: 'Profit track record', options: [NP,
        { v: 'y3', label: '3 straight profitable years', brief: 'At least 3 consecutive profitable years required.' },
        { v: 'y5', label: '5 straight profitable years', brief: 'At least 5 consecutive profitable years required.' },
        { v: 'y10', label: '10 straight profitable years', brief: 'A decade of uninterrupted profitability required — we buy proven businesses only.' },
      ] },
      { key: 'cashConversion', label: 'Cash conversion', options: [NP,
        { v: 'ocf80', label: 'OCF ≥ 80% of profit', brief: 'Operating cash flow must be at least 80% of reported profit — earnings must be real cash.' },
        { v: 'ocf100', label: 'OCF ≥ 100% of profit', brief: 'Operating cash flow must fully cover reported profit; accrual-heavy earnings are a veto.' },
      ] },
    ],
  },
  {
    title: 'Balance sheet',
    fields: [
      { key: 'leverageCap', label: 'Leverage cap', options: [NP,
        { v: 'net_cash', label: 'Net cash only', brief: 'Non-financial holdings must be net cash — zero net debt tolerance.' },
        { v: 'de05', label: 'Debt/equity < 0.5', brief: 'Net debt-to-equity must stay below 0.5x for non-financials.' },
        { v: 'de10', label: 'Debt/equity < 1.0', brief: 'Net debt-to-equity must stay below 1.0x for non-financials.' },
      ] },
      { key: 'interestCoverage', label: 'Interest coverage', options: [NP,
        { v: 'ic3', label: 'EBIT ≥ 3x interest', brief: 'EBIT must cover interest expense at least 3 times.' },
        { v: 'ic6', label: 'EBIT ≥ 6x interest', brief: 'EBIT must cover interest expense at least 6 times.' },
      ] },
    ],
  },
  {
    title: 'Governance & ownership',
    fields: [
      { key: 'promoterHolding', label: 'Promoter skin-in-game', options: [NP,
        { v: 'p40', label: 'Promoters hold > 40%', brief: 'Promoter holding above 40% required — we want owners running the business.' },
        { v: 'p50', label: 'Promoters hold > 50%', brief: 'Promoter holding above 50% required.' },
        { v: 'institutional', label: 'Prefer professional mgmt', brief: 'We prefer professionally managed, board-controlled companies over promoter-driven ones.' },
      ] },
      { key: 'auditFlags', label: 'Audit / governance flags', options: [NP,
        { v: 'zero', label: 'Zero tolerance', brief: 'Any auditor qualification, resignation, or related-party concern is an automatic veto, past or present.' },
        { v: 'case', label: 'Case by case', brief: 'Governance flags are weighed case by case against severity and remediation.' },
      ] },
    ],
  },
  {
    title: 'Universe & liquidity',
    fields: [
      { key: 'mcapFloor', label: 'Market-cap floor', options: [NP,
        { v: 'cr5k', label: '> ₹5,000 cr', brief: 'Market cap above ₹5,000 crore required.' },
        { v: 'cr20k', label: '> ₹20,000 cr', brief: 'Market cap above ₹20,000 crore required.' },
        { v: 'cr50k', label: 'Large caps only (> ₹50k cr)', brief: 'Large caps only: market cap above ₹50,000 crore.' },
      ] },
      { key: 'liquidity', label: 'Liquidity minimum', options: [NP,
        { v: 'adv5', label: '> ₹5 cr daily volume', brief: 'Average daily traded value must exceed ₹5 crore.' },
        { v: 'adv25', label: '> ₹25 cr daily volume', brief: 'Average daily traded value must exceed ₹25 crore — we must be able to exit a full position within days.' },
      ] },
      { key: 'psuStance', label: 'PSU stance', options: [NP,
        { v: 'avoid', label: 'Avoid PSUs', brief: 'We avoid public-sector undertakings — government ownership distorts capital allocation.' },
        { v: 'fine', label: 'PSUs are fine', brief: 'PSU ownership is not a factor; we judge the business on its merits.' },
        { v: 'prefer', label: 'PSUs as value plays', brief: 'We actively look at PSUs as value opportunities where dividends and asset backing compensate for governance risk.' },
      ] },
    ],
  },
  {
    title: 'Entry & exit discipline',
    fields: [
      { key: 'catalyst', label: 'Catalyst requirement', options: [NP,
        { v: 'required', label: 'Identifiable 12-mo catalyst', brief: 'Every entry needs an identifiable catalyst expected to play out within 12 months.' },
        { v: 'optional', label: 'Catalyst not required', brief: 'No catalyst requirement — cheapness and quality are sufficient; time is the catalyst.' },
      ] },
      { key: 'entryStyle', label: 'Entry style', options: [NP,
        { v: 'full', label: 'Full position at entry', brief: 'We take full positions at entry — conviction means sizing up immediately.' },
        { v: 'thirds', label: 'Scale in (thirds)', brief: 'We scale into positions in thirds, adding only as the thesis confirms.' },
      ] },
      { key: 'averagingDown', label: 'Averaging down', options: [NP,
        { v: 'allowed', label: 'Allowed if thesis intact', brief: 'Averaging down is allowed when the thesis is intact and the fall is market-driven, not thesis-driven.' },
        { v: 'never', label: 'Never average down', brief: 'We never average down — a falling position is evidence against the thesis, not a discount.' },
      ] },
      { key: 'exitOnTarget', label: 'At price target', options: [NP,
        { v: 'exit', label: 'Exit fully at target', brief: 'Positions are exited fully when the price target is reached — no anchoring to new highs.' },
        { v: 'trail', label: 'Trail with stops', brief: 'At target we switch to trailing stops and let winners run.' },
        { v: 'rewrite', label: 'Re-underwrite at target', brief: 'At target we re-underwrite from scratch: the position must justify itself at the new price or be sold.' },
      ] },
    ],
  },
  {
    title: 'Risk & portfolio construction',
    fields: [
      { key: 'sectorCap', label: 'Sector concentration', options: [NP,
        { v: 's20', label: 'Max 20% per sector', brief: 'No sector may exceed 20% of the portfolio.' },
        { v: 's30', label: 'Max 30% per sector', brief: 'No sector may exceed 30% of the portfolio.' },
        { v: 'unconstrained', label: 'Concentration OK', brief: 'No sector caps — we concentrate where conviction is highest.' },
      ] },
      { key: 'cashPolicy', label: 'Cash policy', options: [NP,
        { v: 'invested', label: 'Stay fully invested', brief: 'We stay fully invested — timing cash is not our edge.' },
        { v: 'cash20', label: 'Hold up to 20% cash', brief: 'Up to 20% cash is acceptable when opportunities are scarce; cash is a position.' },
      ] },
      { key: 'stopLoss', label: 'Stop-loss discipline', options: [NP,
        { v: 'hard15', label: 'Hard stop at −15%', brief: 'Hard stop-loss at 15% below entry — no thesis survives a 15% drawdown without re-review.' },
        { v: 'hard25', label: 'Hard stop at −25%', brief: 'Hard stop-loss at 25% below entry.' },
        { v: 'thesis', label: 'Thesis-based exits only', brief: 'No hard price stops — exits are driven by thesis impairment, not price action.' },
      ] },
    ],
  },
  {
    title: 'Verdict tie-breakers',
    fields: [
      { key: 'convictionFloor', label: 'Conviction floor', options: [NP,
        { v: 'c7', label: 'Act only at ≥ 7/10', brief: 'We only act on BUY/SELL verdicts with conviction 7/10 or higher; below that, HOLD.' },
        { v: 'c8', label: 'Act only at ≥ 8/10', brief: 'We only act on BUY/SELL verdicts with conviction 8/10 or higher; below that, HOLD.' },
      ] },
      { key: 'downtrendBuys', label: 'Buying in downtrends', options: [NP,
        { v: 'never', label: 'Never catch falling knives', brief: 'We never initiate positions in established downtrends — wait for the base to form.' },
        { v: 'opportunity', label: 'Downtrends = opportunity', brief: 'Established downtrends in quality names are precisely where we initiate — fear creates our entry prices.' },
      ] },
      { key: 'holdBias', label: 'When the call is close', options: [NP,
        { v: 'hold', label: 'Default to HOLD', brief: 'When the evidence is finely balanced, default to HOLD — inaction is cheaper than churn.' },
        { v: 'action', label: 'Default to action', brief: 'When the evidence is finely balanced, prefer action over drift — a weak HOLD is usually a SELL.' },
      ] },
    ],
  },
];

export const DEFAULT_BUILDER = () => ({
  philosophy: {
    style: 'deep_value', valuation: 'strict', horizon: 'long',
    risk: 'conservative', maxPos: '5',
  },
  exclusions: {},
  advanced: {},
  notes: '',
});

/** Compose all selections into the prose brief the AI drafter expands. */
export function composePhilosophyBrief(builder) {
  const { philosophy = {}, exclusions = {}, advanced = {}, notes = '' } = builder || {};
  const parts = [];
  const pick = (key) => PHILOSOPHY_OPTIONS[key].find((o) => o.v === philosophy[key]);
  ['style', 'valuation', 'horizon', 'risk'].forEach((k) => {
    if (pick(k)?.brief) parts.push(pick(k).brief);
  });
  if (philosophy.maxPos) parts.push(`Maximum position size ${philosophy.maxPos}% of NAV at entry.`);
  const excl = EXCLUSION_OPTIONS.filter((e) => exclusions[e.v]).map((e) => e.label.toLowerCase());
  if (excl.length) parts.push(`We never invest in: ${excl.join(', ')}.`);
  ADVANCED_GROUPS.forEach((g) => g.fields.forEach((f) => {
    const sel = f.options.find((o) => o.v === (advanced[f.key] || 'none'));
    if (sel?.brief) parts.push(sel.brief);
  }));
  if (notes && notes.trim()) parts.push(`Additional rules: ${notes.trim()}`);
  return parts.join(' ');
}

const advancedSetCount = (advanced) =>
  Object.values(advanced || {}).filter((v) => v && v !== 'none').length;

// ── Builder UI ───────────────────────────────────────────────

function FieldSelect({ label, value, options, onChange }) {
  return (
    <div className="space-y-1">
      <div className="text-[11px] text-muted-foreground">{label}</div>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-8 text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.v} value={o.v} className="text-xs">
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

/**
 * Structured philosophy builder: core dropdowns, exclusions, an
 * "Advanced rules" accordion covering every overridable lever, and free
 * notes. Controlled via `value` (builder state) / `onChange`.
 */
export function HouseViewBuilder({ value, onChange }) {
  const builder = value || DEFAULT_BUILDER();
  const [advancedOpen, setAdvancedOpen] = useState(advancedSetCount(builder.advanced) > 0);
  const set = (patch) => onChange({ ...builder, ...patch });
  const setPhil = (key, v) => set({ philosophy: { ...builder.philosophy, [key]: v } });
  const setAdv = (key, v) => set({ advanced: { ...builder.advanced, [key]: v } });
  const advCount = advancedSetCount(builder.advanced);

  return (
    <div className="space-y-2.5">
      <div className="grid grid-cols-2 gap-2">
        {[
          ['style', 'Style'],
          ['valuation', 'Valuation discipline'],
          ['horizon', 'Holding horizon'],
          ['risk', 'Risk appetite'],
          ['maxPos', 'Position sizing'],
        ].map(([key, label]) => (
          <FieldSelect
            key={key}
            label={label}
            value={builder.philosophy[key]}
            options={PHILOSOPHY_OPTIONS[key]}
            onChange={(v) => setPhil(key, v)}
          />
        ))}
      </div>

      <div className="space-y-1">
        <div className="text-[11px] text-muted-foreground">Never invest in</div>
        <div className="grid grid-cols-3 gap-1">
          {EXCLUSION_OPTIONS.map((e) => (
            <label key={e.v}
                   className="flex items-center gap-1.5 text-xs cursor-pointer hover:bg-muted/50 rounded px-1 py-0.5">
              <input
                type="checkbox"
                checked={!!builder.exclusions[e.v]}
                onChange={(ev) => set({ exclusions: { ...builder.exclusions, [e.v]: ev.target.checked } })}
              />
              <span className="truncate">{e.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Advanced rules accordion — every lever the committee respects */}
      <div className="rounded-md border border-border">
        <button
          type="button"
          onClick={() => setAdvancedOpen((o) => !o)}
          className="w-full flex items-center gap-1.5 px-2.5 py-2 text-xs font-medium hover:bg-muted/40"
        >
          {advancedOpen
            ? <ChevronDown className="w-3.5 h-3.5" />
            : <ChevronRight className="w-3.5 h-3.5" />}
          Advanced rules (optional)
          {advCount > 0 && (
            <span className="text-[10px] rounded-full bg-primary/10 text-primary px-1.5 py-0.5">
              {advCount} set
            </span>
          )}
          <span className="ml-auto font-normal text-muted-foreground">
            valuation ceilings, quality floors, governance, exits…
          </span>
        </button>
        {advancedOpen && (
          <div className="px-2.5 pb-2.5 space-y-3 border-t border-border pt-2.5">
            <p className="text-[11px] text-muted-foreground leading-relaxed">
              Everything below is optional — "No preference" leaves the call to
              the committee's default judgment. Whatever you set becomes a hard
              rule in your house view, and you can edit any of it later.
            </p>
            {ADVANCED_GROUPS.map((g) => (
              <div key={g.title} className="space-y-1.5">
                <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                  {g.title}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {g.fields.map((f) => (
                    <FieldSelect
                      key={f.key}
                      label={f.label}
                      value={builder.advanced[f.key] || 'none'}
                      options={f.options}
                      onChange={(v) => setAdv(f.key, v)}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Input
        value={builder.notes}
        onChange={(e) => set({ notes: e.target.value })}
        className="h-8 text-xs"
        placeholder="Anything specific (optional) — e.g. ESG screens, sector preferences…"
      />
    </div>
  );
}

// ── Edit dialog ──────────────────────────────────────────────

/**
 * Edit an existing desk's house view: loads the stored builder state +
 * markdown, allows re-drafting with AI, and saves via PUT. The daily
 * fan-out refreshes the desk's overlays once the view changes.
 */
export function EditHouseViewDialog({ tenantId, open, onOpenChange, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [deskName, setDeskName] = useState('');
  const [builder, setBuilder] = useState(DEFAULT_BUILDER());
  const [houseView, setHouseView] = useState('');
  const [drafting, setDrafting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [savedInfo, setSavedInfo] = useState(null);

  useEffect(() => {
    if (!open || !tenantId) return;
    setLoading(true); setError(null); setSavedInfo(null);
    axios.get(`${API}/committee/desks/${tenantId}/house_view`)
      .then((r) => {
        setDeskName(r.data.name || '');
        setHouseView(r.data.house_view_md || '');
        setBuilder(r.data.builder || DEFAULT_BUILDER());
      })
      .catch((e) => setError(e?.response?.data?.detail || 'Could not load house view'))
      .finally(() => setLoading(false));
  }, [open, tenantId]);

  const draftWithAI = async () => {
    if (drafting) return;
    const brief = composePhilosophyBrief(builder);
    if (!brief) return;
    setDrafting(true); setError(null);
    try {
      const { data } = await axios.post(`${API}/committee/desks/draft_house_view`, { brief });
      setHouseView(data.house_view_md || '');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Draft failed');
    } finally {
      setDrafting(false);
    }
  };

  const save = async () => {
    if (saving || houseView.trim().length < 40) return;
    setSaving(true); setError(null);
    try {
      const { data } = await axios.put(`${API}/committee/desks/${tenantId}/house_view`, {
        house_view_md: houseView,
        builder,
      });
      setSavedInfo(data);
      if (onSaved) onSaved();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="w-4 h-4" /> Edit house view{deskName ? ` — ${deskName}` : ''}
          </DialogTitle>
          <DialogDescription>
            Change any rule below — the committee re-judges the shared evidence
            under the updated mandate on the next overlay refresh
            (~₹80–150 per held or watched name).
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-6">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading house view…
          </div>
        ) : (
          <div className="space-y-3">
            <HouseViewBuilder value={builder} onChange={setBuilder} />
            <Button size="sm" variant="outline" onClick={draftWithAI} disabled={drafting}>
              {drafting
                ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                : <Sparkles className="w-3.5 h-3.5 mr-1" />}
              Re-draft house view with AI
            </Button>
            <Textarea
              value={houseView}
              onChange={(e) => setHouseView(e.target.value)}
              rows={10}
              className="font-mono text-xs"
              placeholder="House view (markdown) — this is the document the committee actually reads."
            />
            {error && <div className="text-xs text-red-500">{error}</div>}
            {savedInfo && (
              <div className="text-xs text-emerald-600 dark:text-emerald-400">
                Saved. {savedInfo.overlays_will_refresh > 0
                  ? `${savedInfo.overlays_will_refresh} overlay verdict${savedInfo.overlays_will_refresh === 1 ? '' : 's'} will refresh under the new view on the next fan-out.`
                  : 'New overlays will use the updated view.'}
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          <Button onClick={save} disabled={saving || loading || houseView.trim().length < 40}>
            {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
            Save house view
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
